import clsx from 'clsx';
import { useState, useRef } from 'react';
import { AttachFile } from '@material-ui/icons';
import { ViewAttachmentModal } from './ViewAttachmentsModal';
import { Tooltip } from '../../../components/Tooltip';
import { IconButton } from '../../../components/IconButton';
import { SupportTicketUpdate } from '../../../types/commonTypes';

interface Props {
  message: SupportTicketUpdate;
  ticketId: string;
  messageId: string;
  isStaff: boolean;
  isLastMessage: boolean;
  scrollToMessage(): void;
}

const formatName = (name: string) => {
  const splitName = name.trim().split(/\s+/).filter(Boolean);
  if (!splitName.length) return '?';

  return `${splitName[0][0]}${splitName[splitName.length - 1][0]}`;
};

export const MessageBubble = ({
  message,
  ticketId,
  messageId,
  isStaff,
  isLastMessage,
  scrollToMessage,
}: Props) => {
  const messageRef = useRef<HTMLDivElement>(null);
  const [expandMessage, setExpandMessage] = useState(false);
  const [viewAttachments, setViewAttachments] = useState(false);

  return (
    <div
      id={messageId}
      ref={messageRef}
      className={clsx(
        'max-w-[40%] min-w-[250px] rounded-3xl m-2 overflow-hidden',
        {
          'bg-[rgb(255,152,0,0.1)] border-primary': isStaff,
          'bg-[rgb(69,189,216,0.1)] border-secondary': !isStaff,
          '!border-2': isLastMessage,
        }
      )}
    >
      <div className="inline-flex max-w-full">
        <Tooltip title={message.by} showArrow placement="bottom">
          <span
            className={clsx(
              'text-lg flex items-center justify-center text-white w-[50px] h-[50px] rounded-full p-1 m-2',
              {
                'bg-primary': isStaff,
                'bg-secondary': !isStaff,
              }
            )}
          >
            {isStaff ? 'TG' : formatName(message.by)}
          </span>
        </Tooltip>
        <div
          className={clsx('support-ticket-message mx-4 my-6 shrink', {
            'h-[300px] overflow-hidden': message.message.html.length > 3000,
            '!h-full': expandMessage,
          })}
          dangerouslySetInnerHTML={{
            __html: message.message.html,
          }}
        ></div>
      </div>
      <div className="inline-flex items-center justify-between w-full px-2">
        {message.message.attachments.length > 0 ? (
          <>
            <Tooltip title="View Attachments" showArrow placement="right">
              <IconButton
                className="relative !bg-transparent"
                onClick={() => setViewAttachments(true)}
              >
                <span className="absolute top-0 right-0 flex justify-center items-center rounded-full bg-error text-[10px] w-4 h-4">
                  {message.message.attachments.length}
                </span>
                <AttachFile />
              </IconButton>
            </Tooltip>
            <ViewAttachmentModal
              ticketId={ticketId}
              attachments={message.message.attachments}
              onClose={() => setViewAttachments(false)}
              open={viewAttachments}
            />
          </>
        ) : null}
        <span className="text-xs m-3 ml-auto">{message.timestamp}</span>
      </div>

      {message.message.html.length > 3000 ? (
        <div className="relative flex grow bottom-0 justify-center w-full z-10">
          <button
            className={clsx('w-full py-2 uppercase text-xs', {
              'bg-secondary': !isStaff,
              'bg-primary': isStaff,
            })}
            onClick={() => {
              setExpandMessage(!expandMessage);
              !expandMessage ? null : scrollToMessage();
            }}
          >
            {expandMessage ? 'Collapse Message' : 'Read More...'}
          </button>
        </div>
      ) : null}
    </div>
  );
};
